<script setup lang="ts">
import { t } from '@/utils/i18n';
import { trxWorkspaceSection, TRX_COMPACT_QUERY } from '@/utils/trxNavigation';
import type { NavigationMenuItem } from '@nuxt/ui';
import TrxHeaderControls from './TrxHeaderControls.vue';

const botStore = useBotStore();
const settingsStore = useSettingsStore();
const route = useRoute();
const trxCompact = useMediaQuery(TRX_COMPACT_QUERY);
let pingInterval: ReturnType<typeof setInterval> | undefined;

watchEffect(() => {
  document.title = t('TRX live console');
});
onMounted(() => {
  pingInterval = setInterval(() => {
    void botStore.activeBot?.fetchPing().catch(() => {});
  }, 60000);
});
onBeforeUnmount(() => clearInterval(pingInterval));

const navItems = computed<NavigationMenuItem[]>(() => [
  {
    label: t('Live operation'),
    to: { path: '/trx', query: { section: 'account' } },
    icon: 'i-mdi-view-dashboard',
    active: route.path === '/trx' && trxWorkspaceSection(route.query.section) === 'account',
    ariaCurrentValue:
      route.path === '/trx' && trxWorkspaceSection(route.query.section) === 'account'
        ? 'page'
        : 'false',
  },
  {
    label: t('Research and validation'),
    to: { path: '/trx', query: { section: 'comparison' } },
    icon: 'i-mdi-chart-timeline-variant',
    active: route.path === '/trx' && trxWorkspaceSection(route.query.section) !== 'account',
    ariaCurrentValue:
      route.path === '/trx' && trxWorkspaceSection(route.query.section) !== 'account'
        ? 'page'
        : 'false',
  },
  { label: t('Settings'), to: '/settings', icon: 'i-mdi-cog', active: route.path === '/settings' },
]);
</script>

<template>
  <header>
    <div class="app-nav-shell trx-console-nav flex border-b border-primary">
      <RouterLink
        class="app-nav-brand ms-2 flex flex-row items-center pe-2 gap-2"
        :aria-label="t('TRX live console')"
        to="/trx?section=account"
      >
        <AppIcon class="h-9 w-9" />
        <span class="whitespace-nowrap font-semibold">
          <span class="lg:hidden">TRX</span>
          <span class="hidden lg:inline">{{ t('TRX live console') }}</span>
        </span>
      </RouterLink>
      <div class="app-nav-main flex flex-1 min-w-0 justify-between text-center items-center ms-3">
        <div class="app-nav-navigation items-center hidden md:flex flex-1 min-w-0 gap-3 ms-2">
          <UNavigationMenu
            class="min-w-0"
            :items="navItems"
            variant="link"
            content-orientation="vertical"
            :ui="{
              item: 'py-0 shrink-0',
              link: 'text-md shrink-0 whitespace-nowrap',
              list: 'flex-wrap',
            }"
          />
          <ThemeSelect />
        </div>
        <span
          class="app-nav-timezone shrink-0 ms-auto me-2 text-xs text-muted"
          :title="t('Display and input timezone')"
          >{{ timeZoneLabel(settingsStore.timezone) }}</span
        >
        <div class="app-nav-language me-2 shrink-0"><LanguageSelect /></div>
        <div class="app-nav-account flex me-2">
          <TrxHeaderControls :compact="trxCompact" />
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Give global navigation its own row before links can overlap the account tools. */
@media (min-width: 768px) and (max-width: 1599px) {
  .app-nav-shell {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto auto;
    align-items: center;
  }
  .app-nav-main {
    display: contents;
  }
  .app-nav-brand {
    grid-column: 1;
    grid-row: 1;
  }
  .app-nav-timezone {
    grid-column: 3;
    grid-row: 1;
  }
  .app-nav-language {
    grid-column: 4;
    grid-row: 1;
  }
  .app-nav-account {
    grid-column: 5;
    grid-row: 1;
  }
  .app-nav-navigation {
    grid-column: 1 / -1;
    grid-row: 2;
    margin: 0 8px;
    padding-bottom: 4px;
  }
}
@media (max-width: 767px), (max-width: 1023px) and (max-height: 500px) {
  .trx-console-nav {
    min-height: 52px;
    align-items: center;
  }
  .trx-console-nav .app-nav-navigation,
  .trx-console-nav .app-nav-timezone,
  .trx-console-nav .app-nav-language {
    display: none;
  }
  .trx-console-nav .app-nav-main {
    display: flex;
    justify-content: flex-end;
    margin-left: 0;
  }
  .trx-console-nav .app-nav-brand {
    flex-shrink: 0;
  }
  .trx-console-nav .app-nav-brand :deep(img) {
    width: 28px;
    height: 28px;
  }
}
</style>
